import { __ } from '@wordpress/i18n';
import {
	useBlockProps,
	InspectorControls,
	BlockControls,
	BlockAlignmentToolbar,
} from '@wordpress/block-editor';
import {
	PanelBody,
	Button,
	ToggleControl,
	RangeControl,
	TextControl,
	TextareaControl,
	Placeholder,
	SelectControl,
} from '@wordpress/components';
import { Fragment, useState } from '@wordpress/element';

import './editor.scss';

import iframeUrl from './urlGenerator';
import Iframe from './iframe';

export default function Edit( { attributes, setAttributes, clientId } ) {
	const {
		iframe_url,
		iframe_height,
		iframe_scrolling,
		iframe_allowfullscreen,
		lazyload,
		threshold,
		live,
		alignments,
		iframe_title,
		iframe_caption,
		iframe_id,
		iframe_class,
		iframe_style,
		iframe_name,
		iframe_sandbox,
	} = attributes;

	const videoUrl = iframeUrl( { iframe_url } );

	const [ draftUrl, setDraftUrl ] = useState( '' );

	const alignAttr =
		alignments && String( alignments ).trim().length
			? alignments
			: 'center';

	const alignmentClass = `align${ alignAttr }`;

	const blockProps = useBlockProps( {
		className: [
			'wpxero-iframe',
			`wpxero-iframe-${ clientId }`,
			alignmentClass,
		]
			.filter( Boolean )
			.join( ' ' ),
	} );

	const iframeProps = {
		url: videoUrl,
		height: iframe_height,
		iframe_allowfullscreen,
		iframe_scrolling,
		iframe_title,
		iframe_id,
		iframe_class,
		iframe_style,
		iframe_name,
		iframe_sandbox,
		live,
		threshold,
	};

	return (
		<Fragment>
			<InspectorControls>
				<PanelBody
					title={ __( 'Iframe settings', 'wpxero-iframe' ) }
					initialOpen={ false }
				>
					<TextControl
						label={ __( 'Iframe URL', 'wpxero-iframe' ) }
						help={ __(
							'Paste or type the HTTPS URL you want to embed inside the iframe.',
							'wpxero-iframe'
						) }
						value={ iframe_url }
						placeholder={ __(
							'https://example.com…',
							'wpxero-iframe'
						) }
						onChange={ ( url ) => setAttributes( { iframe_url: url } ) }
					/>

					<TextControl
						label={ __( 'Title attribute (accessible name)', 'wpxero-iframe' ) }
						help={ __(
							'Describe the iframe for screen readers and browser tooltips.',
							'wpxero-iframe'
						) }
						value={ iframe_title }
						onChange={ ( value ) =>
							setAttributes( { iframe_title: value } )
						}
					/>

					<TextareaControl
						label={ __( 'Visible caption (SEO)', 'wpxero-iframe' ) }
						help={ __(
							'Optional text shown under the embed. Search engines and visitors read this even when the iframe is empty or slow to load.',
							'wpxero-iframe'
						) }
						value={ iframe_caption }
						onChange={ ( value ) =>
							setAttributes( { iframe_caption: value } )
						}
						rows={ 3 }
					/>

					<TextControl
						label={ __( 'HTML id', 'wpxero-iframe' ) }
						value={ iframe_id }
						onChange={ ( value ) => setAttributes( { iframe_id: value } ) }
					/>

					<TextControl
						label={ __( 'Extra CSS class', 'wpxero-iframe' ) }
						value={ iframe_class }
						onChange={ ( value ) => setAttributes( { iframe_class: value } ) }
					/>

					<TextControl
						label={ __( 'Iframe name', 'wpxero-iframe' ) }
						value={ iframe_name }
						onChange={ ( value ) => setAttributes( { iframe_name: value } ) }
					/>

					<TextControl
						label={ __( 'Sandbox directive list', 'wpxero-iframe' ) }
						help={ __(
							'Optional space-separated sandbox tokens such as allow-scripts.',
							'wpxero-iframe'
						) }
						value={ iframe_sandbox }
						onChange={ ( value ) =>
							setAttributes( { iframe_sandbox: value } )
						}
					/>

					<TextControl
						label={ __( 'Inline CSS (optional)', 'wpxero-iframe' ) }
						help={ __(
							'Short inline styles applied to the iframe element.',
							'wpxero-iframe'
						) }
						value={ iframe_style }
						onChange={ ( value ) => setAttributes( { iframe_style: value } ) }
					/>

					<RangeControl
						label={ __( 'Height (px)', 'wpxero-iframe' ) }
						help={ __(
							'Height of the iframe in pixels.',
							'wpxero-iframe'
						) }
						value={ iframe_height }
						onChange={ ( value ) =>
							setAttributes( { iframe_height: value } )
						}
						min={ 200 }
						max={ 1200 }
					/>

					<SelectControl
						label={ __( 'Scrolling', 'wpxero-iframe' ) }
						help={ __(
							'Whether scrollbars are shown inside legacy browsers.',
							'wpxero-iframe'
						) }
						value={ iframe_scrolling }
						options={ [
							{
								label: __( 'Auto', 'wpxero-iframe' ),
								value: 'auto',
							},
							{
								label: __( 'Yes', 'wpxero-iframe' ),
								value: 'yes',
							},
							{
								label: __( 'No', 'wpxero-iframe' ),
								value: 'no',
							},
						] }
						onChange={ ( value ) =>
							setAttributes( { iframe_scrolling: value } )
						}
					/>

					<ToggleControl
						label={ __( 'Allow fullscreen', 'wpxero-iframe' ) }
						checked={ iframe_allowfullscreen }
						onChange={ ( value ) =>
							setAttributes( { iframe_allowfullscreen: value } )
						}
					/>

					<ToggleControl
						label={ __( 'Lazy-load', 'wpxero-iframe' ) }
						help={ __(
							'Loads the iframe near the viewport; uses native lazy-loading plus a small script enhancement.',
							'wpxero-iframe'
						) }
						checked={ lazyload }
						onChange={ () => setAttributes( { lazyload: ! lazyload } ) }
					/>

					{ lazyload && (
						<>
							<RangeControl
								label={ __( 'Early load margin (px)', 'wpxero-iframe' ) }
								help={ __(
									'Extra vertical margin before loading (IntersectionObserver root margin).',
									'wpxero-iframe'
								) }
								value={ threshold }
								onChange={ ( value ) =>
									setAttributes( { threshold: value } )
								}
								min={ 0 }
								max={ 1000 }
							/>

							<ToggleControl
								label={ __( 'Observe dynamically added iframes', 'wpxero-iframe' ) }
								checked={ live }
								onChange={ () =>
									setAttributes( { live: ! live } )
								}
								help={ __(
									'Keep observing if the block is duplicated via AJAX-heavy layouts.',
									'wpxero-iframe'
								) }
							/>
						</>
					) }
				</PanelBody>
			</InspectorControls>

			<BlockControls>
				<BlockAlignmentToolbar
					value={ alignments }
					onChange={ ( next ) =>
						setAttributes( { alignments: next ?? 'center' } )
					}
				/>
			</BlockControls>

			<div { ...blockProps }>
				{ ! iframe_url && (
					<Placeholder
						style={ { display: 'block', width: '100%' } }
						label={
							<>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="#000000"
									width={ 28 }
									height={ 28 }
									viewBox="0 0 24 24"
								>
									<path
										fillRule="evenodd"
										d="M13,20 L20,20 L20,4 L4,4 L4,11 L11,11 C12.1045695,11 13,11.8954305 13,13 L13,20 Z M11,20 L11,13 L4,13 L4,20 L11,20 Z M4,2 L20,2 C21.1045695,2 22,2.8954305 22,4 L22,20 C22,21.1045695 21.1045695,22 20,22 L4,22 C2.8954305,22 2,21.1045695 2,20 L2,4 C2,2.8954305 2.8954305,2 4,2 Z"
									/>
								</svg>
								<p>{ __( 'WPXero Iframe', 'wpxero-iframe' ) }</p>
							</>
						}
					>
						<div className="wpxero-iframe--warning flex-container">
							<TextControl
								className="wpxero-input-url"
								label={ __( 'Iframe URL', 'wpxero-iframe' ) }
								help={ __(
									'Paste the URL you want to display on your site.',
									'wpxero-iframe'
								) }
								placeholder={ __(
									'https://…',
									'wpxero-iframe'
								) }
								value={ draftUrl }
								onChange={ setDraftUrl }
							/>

							<Button
								className="wpxero-submit-button"
								variant="primary"
								onClick={ () => {
									setAttributes( { iframe_url: draftUrl } );
								} }
								disabled={ ! draftUrl }
							>
								{ __( 'Embed', 'wpxero-iframe' ) }
							</Button>
						</div>
					</Placeholder>
				) }

				{ iframe_url && (
					<div className="wpxero-iframe--editor">
						<figure className="wpxero-iframe-figure">
							<Iframe
								{ ...iframeProps }
								className={
									lazyload ? 'lazy' : ''
								}
							/>
							{ iframe_caption &&
								String( iframe_caption ).trim() && (
									<figcaption className="wpxero-iframe-caption">
										{ String( iframe_caption ).trim() }
									</figcaption>
								) }
						</figure>
					</div>
				) }
			</div>
		</Fragment>
	);
}
